import React, { useState, useEffect } from 'react';
import { adminApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { 
  Building, Users, Calendar, AlertTriangle, TrendingUp, 
  DollarSign, Loader2, CheckCircle, Clock 
} from 'lucide-react';
import { formatCurrency } from '../../lib/utils';

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await adminApi.getMetrics();
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-farmhouse-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-farmhouse-saddle animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-farmhouse-charcoal pt-8 pb-16" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif text-4xl font-semibold text-farmhouse-cream mb-8">
          Admin Dashboard
        </h1>

        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="metric-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-md bg-farmhouse-saddle/20">
                <Building className="w-5 h-5 text-farmhouse-saddle" />
              </div>
              <span className="text-farmhouse-muted text-sm">Total Listings</span>
            </div>
            <p className="metric-value">{metrics?.listings?.total || 0}</p>
            <div className="flex gap-4 mt-2 text-sm">
              <span className="text-green-400">{metrics?.listings?.approved || 0} approved</span>
              <span className="text-farmhouse-gold">{metrics?.listings?.pending || 0} pending</span>
            </div>
          </div>

          <div className="metric-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-md bg-farmhouse-moss/20">
                <Users className="w-5 h-5 text-green-400" />
              </div>
              <span className="text-farmhouse-muted text-sm">Total Users</span>
            </div>
            <p className="metric-value">{metrics?.users?.total || 0}</p>
            <div className="flex gap-4 mt-2 text-sm text-farmhouse-muted">
              <span>{metrics?.users?.hosts || 0} hosts</span>
              <span>{metrics?.users?.guests || 0} guests</span>
            </div>
          </div>

          <div className="metric-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-md bg-farmhouse-gold/20">
                <Calendar className="w-5 h-5 text-farmhouse-gold" />
              </div>
              <span className="text-farmhouse-muted text-sm">Bookings</span>
            </div>
            <p className="metric-value">{metrics?.bookings?.total || 0}</p>
            <div className="flex gap-4 mt-2 text-sm">
              <span className="text-green-400">{metrics?.bookings?.completion_rate || 0}% completion</span>
            </div>
          </div>

          <div className="metric-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-md bg-farmhouse-clay/20">
                <AlertTriangle className="w-5 h-5 text-farmhouse-clay" />
              </div>
              <span className="text-farmhouse-muted text-sm">Open Disputes</span>
            </div>
            <p className="metric-value">{metrics?.disputes?.open || 0}</p>
            <p className="text-sm text-farmhouse-muted mt-2">Requires attention</p>
          </div>
        </div>

        {/* Listings by Region */}
        {metrics?.listings?.by_state && Object.keys(metrics.listings.by_state).length > 0 && (
          <div className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6 mb-8">
            <h2 className="font-serif text-xl font-semibold text-farmhouse-cream mb-4">
              Listings by State
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Object.entries(metrics.listings.by_state).map(([state, count]) => (
                <div key={state} className="text-center p-4 bg-farmhouse-charcoal rounded-md">
                  <p className="text-2xl font-serif font-semibold text-farmhouse-cream">{count}</p>
                  <p className="text-farmhouse-muted text-sm">{state}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <a href="/admin/listings?status=submitted" className="metric-card hover:border-farmhouse-saddle transition-colors group">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-md bg-farmhouse-gold/20">
                <Clock className="w-6 h-6 text-farmhouse-gold" />
              </div>
              <div>
                <p className="text-farmhouse-cream font-medium group-hover:text-farmhouse-saddle transition-colors">
                  Review Pending Listings
                </p>
                <p className="text-farmhouse-muted text-sm">
                  {metrics?.listings?.pending || 0} waiting for approval
                </p>
              </div>
            </div>
          </a>

          <a href="/admin/disputes" className="metric-card hover:border-farmhouse-saddle transition-colors group">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-md bg-farmhouse-clay/20">
                <AlertTriangle className="w-6 h-6 text-farmhouse-clay" />
              </div>
              <div>
                <p className="text-farmhouse-cream font-medium group-hover:text-farmhouse-saddle transition-colors">
                  Resolve Disputes
                </p>
                <p className="text-farmhouse-muted text-sm">
                  {metrics?.disputes?.open || 0} open disputes
                </p>
              </div>
            </div>
          </a>

          <a href="/admin/users" className="metric-card hover:border-farmhouse-saddle transition-colors group">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-md bg-farmhouse-moss/20">
                <Users className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-farmhouse-cream font-medium group-hover:text-farmhouse-saddle transition-colors">
                  Manage Users
                </p>
                <p className="text-farmhouse-muted text-sm">
                  {metrics?.users?.total || 0} registered users
                </p>
              </div>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}
